import { read, write } from './local-storage'
import { debug } from './logger'

/**
 * First-touch acquisition attribution (paid search qualification)
 * Captured at boot from the landing URL / referrer / Google Ads cookie, then kept until expiry
 * so it is still known when the inquiry is submitted. Only a referrer-only record can be replaced
 * later, by a visit carrying campaign data (gclid / utm_*).
 */
const ATTRIBUTION_STORAGE_KEY = 'stores.attribution'
const ATTRIBUTION_TTL = 90 * 24 * 60 * 60 * 1000
const REFERRER_MAX_LENGTH = 512
const GOOGLE_CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid']
const GOOGLE_CLICK_ID_COOKIE = '_gcl_aw'

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined' && !!window.localStorage

const searchOf = (url) => {
  const start = url.indexOf('?')
  if (start === -1) { return '' }
  const end = url.indexOf('#', start)
  return end === -1 ? url.slice(start) : url.slice(start, end)
}

const readParams = (search) => {
  const params = new window.URLSearchParams(search)
  const clickId = GOOGLE_CLICK_ID_PARAMS.map((name) => params.get(name)).find((value) => value)
  return {
    gclid: clickId || null,
    utmSource: params.get('utm_source') || null,
    utmMedium: params.get('utm_medium') || null,
    utmCampaign: params.get('utm_campaign') || null
  }
}

// Google Ads conversion linker cookie, formatted as GCL.<timestamp>.<gclid>
const readGoogleClickIdCookie = () => {
  const cookie = (document.cookie || '')
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.indexOf(`${GOOGLE_CLICK_ID_COOKIE}=`) === 0)
  if (!cookie) { return null }
  const segments = decodeURIComponent(cookie.slice(GOOGLE_CLICK_ID_COOKIE.length + 1)).split('.')
  return segments[segments.length - 1] || null
}

const hasCampaignData = (attribution) => !!(attribution && (
  attribution.gclid ||
  attribution.utmSource ||
  attribution.utmMedium ||
  attribution.utmCampaign
))

const isMeaningful = (attribution) => hasCampaignData(attribution) || !!(attribution && attribution.referrer)

const isExpired = (attribution) => !attribution.capturedAt || (attribution.capturedAt + ATTRIBUTION_TTL) < Date.now()

const readStored = () => {
  const stored = read(ATTRIBUTION_STORAGE_KEY)
  return (isMeaningful(stored) && !isExpired(stored)) ? stored : null
}

const readCurrent = () => {
  const referrer = document.referrer || ''
  const fromLocation = readParams(window.location.search)
  const fromReferrer = readParams(searchOf(referrer))
  return {
    gclid: fromLocation.gclid || fromReferrer.gclid || readGoogleClickIdCookie(),
    utmSource: fromLocation.utmSource || fromReferrer.utmSource,
    utmMedium: fromLocation.utmMedium || fromReferrer.utmMedium,
    utmCampaign: fromLocation.utmCampaign || fromReferrer.utmCampaign,
    referrer: referrer ? referrer.slice(0, REFERRER_MAX_LENGTH) : null,
    capturedAt: Date.now()
  }
}

export const captureAttribution = () => {
  try {
    if (!isBrowser()) { return }
    // First-touch: an existing (non expired) attribution carrying campaign data wins over the current visit
    const stored = readStored()
    if (stored && hasCampaignData(stored)) { return }
    const attribution = readCurrent()
    // A referrer-only record (typically a WordPress page of the same site, the SPA living under /app)
    // is still upgraded by a later visit carrying campaign data (gclid / utm_*)
    if (stored ? hasCampaignData(attribution) : isMeaningful(attribution)) {
      debug(`[utilities][attribution.captureAttribution] Capturing first-touch attribution (gclid: ${!!attribution.gclid}, utm_medium: ${attribution.utmMedium || '-'})`)
      write(ATTRIBUTION_STORAGE_KEY, attribution)
    }
  } catch (e) {
    debug(`[utilities][attribution.captureAttribution] Unable to capture attribution: ${e}`)
  }
}

export const getAttribution = () => {
  try {
    if (!isBrowser()) { return null }
    const stored = readStored()
    if (!stored) { return null }
    return {
      gclid: stored.gclid || null,
      utmSource: stored.utmSource || null,
      utmMedium: stored.utmMedium || null,
      utmCampaign: stored.utmCampaign || null,
      referrer: stored.referrer || null
    }
  } catch (e) {
    debug(`[utilities][attribution.getAttribution] Unable to read attribution: ${e}`)
    return null
  }
}



// WEBPACK FOOTER //
// ./src/utilities/attribution.js